import React from "react";

// Modules
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// App
import { serverUrl, favoritesEnabled, counterEnabled } from "../../config";
import { getExternal } from "../../core/getNode";
import Pagination from "../../partials/pagination";
import PageHeader from "../../partials/pageHeader";
import { alertMessages } from "../../partials/alertMessages";
import Error from "../../partials/error";
import NoData from "../../partials/noData";
import { decodeHtmlEntities, getFavoritesByContentType, favoritesContentType, } from "../../utils/helpers";
import BlogItem from "./blogItem";
import { patchNode } from "../../core/postNode";


// UI components
import Row from "react-bootstrap/Row";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Input,
} from "@mui/material";
import CompanyappLoader from "../../partials/companyappLoader";

const pageSize = 24;
const contentType = favoritesContentType('blogs');

export default function Blogs(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(0);
  const [categoryOpen, setCategoryOpen] = useState(false);

  const user = useSelector((state) => state).authReducer.user;
  const favorites = useSelector((state) => state).favoritesReducer;
  const favStrings = getFavoritesByContentType(favorites, contentType, true);
  const counter = useSelector((state) => state).counterReducer;

  // hooks
  const dispatch = useDispatch();

  useEffect(() => {
    if (counterEnabled) {
      updateRemoteCounter();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (categories.length <= 1) {
      getCategories();
    }
    if ((activeIndex === 1 && favStrings !== "") || activeIndex === 0) {
      getContent(1);
    }
    else {
      setData([]);
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, [selectedCategoryItem, activeIndex]);

  useEffect(() => {
    if (activeIndex === 1) {
      if (favStrings !== "") {
        getContent(1);
      }
      else {
        setData([]);
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [favStrings]);

  /**
   * @function setError
   * @description Updates the state of the component upon an error
   * @param {boolean} _isError
   * @param {int} _errorStatus
   * @param {string} _errorMessage
   */
  const _setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);

    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  /**
   * @function getContent
   * @description Retrieves the data from an API / Fallback to local realm object if there is no connection
   */
  const getContent = (_currentPage) => {
    let path = `https://companyapp.co.uk/wp-json/wp/v2/posts?per_page=${pageSize}&page=${_currentPage}&_embed`;

    const tid = selectedCategory;

    if (activeIndex === 1) {
      path = `https://companyapp.co.uk/wp-json/wp/v2/posts?per_page=${pageSize}&page=${_currentPage}&_embed&include=${favStrings}`;
    } else {
      if (tid !== 0) {
        path = `https://companyapp.co.uk/wp-json/wp/v2/posts?categories=${tid}&per_page=${pageSize}&page=${_currentPage}&_embed`;
      }
    }

    getExternal(path)
      .then((response) => {
        setData(response.data);
        setTotalItems(response.headers["x-wp-total"]);
        setIsLoading(false);
      })
      .catch((_error) => {
        if (_error.response) {
          _setError(true, _error.response.status, _error.response.statusText);
        } else if (_error.request) {
          _setError(true, 0, alertMessages.requestError.message);
        } else {
          _setError(true, 0, alertMessages.unkownError.message);
        }
      });
  };

  /**
   * @function getCategories
   * @description Retrives the category data from an API / Fallback to local realm object if there is no connection
   */
  const getCategories = () => {
    getExternal("https://companyapp.co.uk/wp-json/wp/v2/categories")
      .then((response) => {
        renderCategories(response.data);
      })
      .catch((_error) => {
        if (_error.response) {
          _setError(true, _error.response.status, _error.response.statusText);
        } else if (_error.request) {
          _setError(true, 0, alertMessages.requestError.message);
        } else {
          _setError(true, 0, alertMessages.unkownError.message);
        }
      });
  };

  /**
   * @function onPaginate
   * @description Pagination callback
   * @param {int} _index - Page number
   */
  const onPaginate = (_index) => {
    setIsLoading(true);

    setCurrentPage(_index);
    getContent(_index);
  };

  /**
   * @function renderCategories
   * @description Updates the state with the retrived categories
   * @param {object} _categories
   */
  const renderCategories = (_data) => {
    let categories = [
      {
        value: 0,
        label: "All Blogs",
      },
    ];

    _data.forEach((item) => {
      categories.push({
        value: item.id,
        label: item.name,
      });
    });

    setCategories(categories);
  };

  /**
   * @function handleCategorySelection
   * @description Modal Picker callback / Updates the header title and sets the state to the selected category
   * @param {string} _id - Category ID
   */
  const handleCategorySelection = (value) => {
    setIsLoading(true);

    setIsError(false);
    setCurrentPage(1);
    setSelectedCategory(parseInt(value, 10));
    setSelectedCategoryItem(value);
    setCategoryOpen(false);
  };

  // Pagination component
  const renderPagination = () => {
    if (totalItems <= pageSize) {
      return null;
    } else {
      return (
        <Pagination
          owner="client"
          containerClasses={`creator-pagination d-flex justify-content-center align-items-center mt-4`}
          previousDisabled={currentPage === 1 ? true : false}
          nextDisabled={data.length < pageSize ? true : false}
          currentPage={currentPage}
          onPrevious={() => {
            onPaginate(currentPage - 1);
          }}
          onNext={() => {
            onPaginate(currentPage + 1);
          }}
        />
      );
    }
  };

  const getCategoryName = (id) => {
    let categoryName = categories.find((e) => e.value === id);
    return categoryName ? categoryName.label : "";
  };

  // Data component
  const dataComponent = () => {
    if (typeof data !== "undefined" && data.length > 0) {
      return (
        <Row>
          {data.map((item, index) => {
            let catName = getCategoryName(item.categories[0]);
            return (
              <BlogItem
                key={`blog-${index}`}
                item={item}
                categoryName={catName}
                contentType={contentType}
              />
            );
          })}
        </Row>
      );
    } else {
      return <NoData activeIndex={activeIndex} />;
    }
  };

  // Updates content counter
  const updateRemoteCounter = () => {
    const updateData = {
      contentType: contentType,
      acknowledgedCount: counter?.contentCounts?.blogs || 0,
    };

    patchNode(`${serverUrl}/api/v2/counter`, updateData, user.csrf_token)
      .then((response) => {
        if (response.data) {
          dispatch({ type: "UPDATE_COUNTER", payload: response.data });
        }
      })
      .catch((error) => {
      });
  };

  return (
    <>
      {isError ? (
        <Error status={errorStatus} message={errorMessage} />
      ) : (
        <main className="blogs screen">
          <PageHeader
            pageName="Company Blogs"
            filters={true}
            owner="client"
            pageNameColor="clientText.dark"
            enableFavorites={favoritesEnabled}
            categorySelector={
              <FormControl variant="standard" fullWidth size="small">
                <InputLabel sx={{ fontSize: 14 }}>Blogs</InputLabel>
                <Select
                  labelId="blogs-category-select-label"
                  id="blogs-category-select"
                  value={activeIndex === 1 ? 0 : selectedCategoryItem}
                  disabled={activeIndex === 1}
                  label="Blogs"
                  open={categoryOpen}
                  onClose={() => setCategoryOpen(false)}
                  onOpen={() => setCategoryOpen(true)}
                  input={
                    <Input
                      sx={{
                        fontSize: 14,
                        "&.MuiInput-underline:before": {
                          borderBottomColor: "#d2d0cf",
                        },
                      }}
                      label="Blogs"
                    />
                  }
                >
                  {categories.map((category, index) => {
                    return (
                      <MenuItem
                        key={`blogs-category-${category.value}`}
                        value={category.value}
                        onClick={() => {
                          handleCategorySelection(category.value);
                        }}
                      >
                        {decodeHtmlEntities(category.label)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            }
            categorySelectorDisabled={activeIndex === 1}
            getContent={() => {
              setActiveIndex(0);
              setIsLoading(true);
              getContent(1);
            }}
            getFavourites={() => {
              setActiveIndex(activeIndex === 0 ? 1 : 0);
              setIsLoading(true);
            }}
            activeTabIndex={activeIndex}
          />
          {isLoading ? (
            <CompanyappLoader />
          ) : (
            <>
              {dataComponent()}
              {renderPagination()}
            </>
          )}
        </main>
      )}
    </>
  );
}
